import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { ReportHead, Empty } from '../components/Shell.jsx';
import {
  canPreview, kindLabel, lastHitLabel, reorderPatches, RULE_EXAMPLES, RULE_KINDS, sortRules,
} from '../lib/descriptionRules.js';

const emptyForm = { kind: 'regex', pattern: '', note: '' };
const SAMPLE_HINT = 'POS PURCHASE (EFFEC 05082026) Tops';

/**
 * Description clean-up rules: noise a bank puts in front of (or inside) the
 * words a bookkeeper actually reads — `POS PURCHASE (EFFEC 05082026) Tops` —
 * removed when a statement is imported. They run top to bottom, and the
 * description as the bank wrote it is KEPT on the line (`descriptionOriginal`),
 * which the cash book shows behind a small icon.
 *
 * Modelled on AllocationRules.jsx, and like it DELETE deactivates rather than
 * removes: a rule that changed historic lines still explains them.
 *
 * THE TEST BOX ASKS THE SERVER. `POST .../description-rules/preview` runs the
 * same `applyDescriptionRules` the import runs, so what is shown here is what
 * the import will do — the browser never evaluates a pattern itself. It tests
 * whichever rule is under the cursor: the one being edited, else the new one.
 */
export function DescriptionRules({ entity }) {
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // { id, kind, pattern, note }
  const [showInactive, setShowInactive] = useState(false);
  const [sample, setSample] = useState('');
  const [preview, setPreview] = useState(null);  // { key, cleaned, valid, error } | { key, failed }
  const [formErr, setFormErr] = useState(null);   // INVALID_PATTERN, shown beside the pattern
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/entities/${entity.id}/description-rules`)
    .then((d) => setRules(d.rules)).catch((e) => setErr(e.message));

  useEffect(() => { if (entity?.id) load(); }, [entity?.id]); // eslint-disable-line

  // What the test box is testing right now.
  const under = editing ?? form;
  const testKey = `${under.kind}\u0000${under.pattern}\u0000${sample}`;
  const askable = canPreview({ pattern: under.pattern, sample });

  // Debounced ~300ms, and keyed: an answer is only shown while it is still the
  // answer to what is typed, so a slow reply for an older pattern never shows.
  useEffect(() => {
    if (!askable) { setPreview(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.post(`/api/entities/${entity.id}/description-rules/preview`,
        { kind: under.kind, pattern: under.pattern, sample })
        .then((r) => { if (!cancelled) setPreview({ key: testKey, ...r }); })
        .catch((e) => { if (!cancelled) setPreview({ key: testKey, failed: e.message }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [entity?.id, testKey, askable]); // eslint-disable-line

  const shown = askable && preview?.key === testKey ? preview : null;

  const create = async () => {
    setBusy(true); setErr(null); setMsg(null); setFormErr(null);
    try {
      await api.post(`/api/entities/${entity.id}/description-rules`, {
        kind: form.kind,
        pattern: form.pattern,
        note: form.note.trim() || undefined,
        // After the last one, so a new rule runs last until it is moved.
        sortOrder: (sortRules(rules).at(-1)?.sortOrder ?? 0) + 10,
      });
      setMsg('Rule created.');
      setForm(emptyForm);
      load();
    } catch (e) {
      if (e.code === 'INVALID_PATTERN') setFormErr(e.message); else setErr(e.message);
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true); setErr(null); setMsg(null); setFormErr(null);
    try {
      await api.patch(`/api/entities/${entity.id}/description-rules/${editing.id}`, {
        kind: editing.kind,
        pattern: editing.pattern,
        // Blank clears the note — PATCH is explicit about null versus absent.
        note: editing.note.trim() || null,
      });
      setMsg('Rule updated.');
      setEditing(null);
      load();
    } catch (e) {
      if (e.code === 'INVALID_PATTERN') setFormErr(e.message); else setErr(e.message);
    } finally { setBusy(false); }
  };

  const setActive = async (rule, active) => {
    setErr(null); setMsg(null);
    try {
      if (active) await api.patch(`/api/entities/${entity.id}/description-rules/${rule.id}`, { active: true });
      else await api.del(`/api/entities/${entity.id}/description-rules/${rule.id}`);
      load();
    } catch (e) { setErr(e.message); }
  };

  const move = async (rule, direction, visibleIds) => {
    const patches = reorderPatches(rules, rule.id, direction, visibleIds);
    if (patches.length === 0) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      // One at a time and in order: they are a renumbering of one list, and
      // the list read back afterwards is the server's.
      for (const p of patches) {
        // eslint-disable-next-line no-await-in-loop
        await api.patch(`/api/entities/${entity.id}/description-rules/${p.id}`, { sortOrder: p.sortOrder });
      }
    } catch (e) { setErr(e.message); } finally { setBusy(false); load(); }
  };

  if (!rules) return <div className="empty">{err ? <div className="error">{err}</div> : 'Loading…'}</div>;

  const ordered = sortRules(rules);
  const visible = ordered.filter((r) => showInactive || r.active);
  const visibleIds = new Set(visible.map((r) => r.id));

  return (
    <>
      <ReportHead entity={entity} title="Description clean-up"
                  sub="Take the noise out of bank statement descriptions when they are imported." />

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="note">
        Bank statements wrap the part you care about in reference noise. A rule removes a piece of
        it when a statement is imported — rules run <strong>top to bottom</strong>, each one on
        what the one above left. The description exactly as the bank wrote it is <strong>kept</strong>:
        a line that was changed shows a small ↺ beside its description, and clicking it shows the
        original.
      </div>

      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>New rule</h2>
      <div className="form-grid">
        <label className="field"><span>Type</span>
          <select value={form.kind} onChange={(e) => { setFormErr(null); setForm({ ...form, kind: e.target.value }); }}>
            {RULE_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select></label>
        <label className="field"><span>{form.kind === 'regex' ? 'Pattern' : 'Text to remove'}</span>
          <input className="input--figure" value={form.pattern} spellCheck={false}
                 placeholder={form.kind === 'regex' ? 'POS PURCHASE \\(EFFEC \\d+\\)' : 'CREDIT TRANSFER'}
                 onChange={(e) => { setFormErr(null); setForm({ ...form, pattern: e.target.value }); }} /></label>
        <label className="field"><span>Note</span>
          <input value={form.note} placeholder="Optional — why this rule exists"
                 onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
      </div>
      {formErr && !editing && <div className="error">{formErr}</div>}

      <TestBox
        target={editing ? 'the rule you are editing' : 'the new rule above'}
        sample={sample} onSample={setSample} shown={shown}
        needPattern={under.pattern.trim() === ''} />

      <button className="btn" style={{ marginTop: 8 }} onClick={create}
              disabled={busy || !form.pattern.trim()}>
        {busy ? 'Saving…' : 'Add rule'}
      </button>

      <div className="note" style={{ marginTop: 16 }}>
        <strong>Examples</strong> — click one to fill the form and test it:
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {RULE_EXAMPLES.map((x) => (
            <li key={x.pattern} style={{ margin: '3px 0' }}>
              {kindLabel(x.kind)} <code>{x.pattern}</code>{' '}
              <button className="icon-btn icon-btn--tight" type="button"
                      onClick={() => { setEditing(null); setFormErr(null);
                        setForm({ kind: x.kind, pattern: x.pattern, note: form.note }); setSample(x.sample); }}>
                Use
              </button>
            </li>
          ))}
        </ul>
      </div>

      <h2 className="eyebrow" style={{ margin: '30px 0 10px' }}>Rules, in the order they run</h2>
      <div className="controls">
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <span>Show deactivated rules</span>
        </label>
      </div>

      {visible.length === 0 ? (
        <Empty><p>No clean-up rules yet.</p><p>Add one above; test it on a real description first.</p></Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 84 }}>Order</th>
                <th style={{ width: 130 }}>Type</th>
                <th>Pattern</th>
                <th>Note</th>
                <th className="num" style={{ width: 60 }}>Hits</th>
                <th style={{ width: 110 }}>Last hit</th>
                <th style={{ width: 90 }}>Active</th>
                <th style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.id} style={r.active ? undefined : { opacity: 0.55 }}>
                  <td className="figure">
                    <span style={{ marginRight: 6 }}>{i + 1}</span>
                    <button className="icon-btn icon-btn--tight" disabled={busy || i === 0}
                            title="Run earlier" aria-label="Move up"
                            onClick={() => move(r, 'up', visibleIds)}>▲</button>{' '}
                    <button className="icon-btn icon-btn--tight" disabled={busy || i === visible.length - 1}
                            title="Run later" aria-label="Move down"
                            onClick={() => move(r, 'down', visibleIds)}>▼</button>
                  </td>
                  {editing?.id === r.id ? (
                    <>
                      <td>
                        <select value={editing.kind} style={{ width: '100%' }}
                                onChange={(e) => { setFormErr(null); setEditing({ ...editing, kind: e.target.value }); }}>
                          {RULE_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </td>
                      <td>
                        <input className="input--figure" style={{ width: '100%' }} value={editing.pattern}
                               spellCheck={false}
                               onChange={(e) => { setFormErr(null); setEditing({ ...editing, pattern: e.target.value }); }} />
                        {formErr && <div className="neg" style={{ fontSize: 11, marginTop: 4 }}>{formErr}</div>}
                      </td>
                      <td>
                        <input style={{ width: '100%' }} value={editing.note} placeholder="Note"
                               onChange={(e) => setEditing({ ...editing, note: e.target.value })} />
                      </td>
                      <td className="num figure">{r.hits}</td>
                      <td className="figure" style={{ fontSize: 12 }}>{lastHitLabel(r.lastHitAt)}</td>
                      <td>{r.active ? 'Active' : 'Off'}</td>
                      <td className="num">
                        <button className="icon-btn" onClick={saveEdit}
                                disabled={busy || !editing.pattern.trim()}>Save</button>{' '}
                        <button className="icon-btn" onClick={() => { setEditing(null); setFormErr(null); }}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{kindLabel(r.kind)}</td>
                      <td><code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{r.pattern}</code></td>
                      <td style={{ fontSize: 12 }}>{r.note ?? ''}</td>
                      <td className="num figure">
                        {r.hits}
                        {r.hits === 0 && r.active && (
                          <div style={{ fontSize: 11 }} className="neg">never fired</div>
                        )}
                      </td>
                      <td className="figure" style={{ fontSize: 12 }}>{lastHitLabel(r.lastHitAt)}</td>
                      <td>{r.active ? 'Active' : <span className="neg">Deactivated</span>}</td>
                      <td className="num">
                        <button className="icon-btn" onClick={() => {
                          setFormErr(null);
                          setEditing({ id: r.id, kind: r.kind, pattern: r.pattern, note: r.note ?? '' });
                        }}>Edit</button>{' '}
                        <button className="icon-btn" onClick={() => setActive(r, !r.active)}>
                          {r.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note">
        Order matters: a rule that removes <code>POS PURCHASE</code> before another that expects it
        will leave the second with nothing to do. Deactivating keeps a rule on file, because lines it
        changed still carry the original description.
      </div>
    </>
  );
}

/** Sample in, cleaned description out — straight from the server. */
function TestBox({ target, sample, onSample, shown, needPattern }) {
  return (
    <div className="note" style={{ marginTop: 4 }}>
      <label className="field"><span>Test — paste a description from a statement ({target})</span>
        <input value={sample} spellCheck={false} placeholder={SAMPLE_HINT}
               onChange={(e) => onSample(e.target.value)} /></label>
      <div style={{ marginTop: 8, minHeight: 20 }} aria-live="polite">
        {needPattern || sample === '' ? (
          <span className="cashbook__unknown">
            {needPattern ? 'Type a pattern above to see what it does.' : 'Type a sample above to see the result.'}
          </span>
        ) : !shown ? (
          <span className="cashbook__unknown">Testing…</span>
        ) : shown.failed ? (
          <span className="neg">Could not test: {shown.failed}</span>
        ) : shown.valid === false ? (
          <span className="neg"><strong>Not a valid pattern:</strong> {shown.error}</span>
        ) : (
          <>
            <code>{sample}</code> → <strong><code>{shown.cleaned === '' ? '(nothing left)' : shown.cleaned}</code></strong>
            {shown.cleaned === sample && <span className="cashbook__unknown"> — unchanged: this rule does not match that text</span>}
          </>
        )}
      </div>
    </div>
  );
}
